import type { ReactNode } from 'react';
import { Edit2 } from 'lucide-react';
import { ThemedCard } from './themed';

export interface InfoPanelProps {
  /** Section title */
  title: string;

  /** Optional subtitle/description */
  subtitle?: string;

  /** Can this panel be edited? (default: true) */
  isEditable?: boolean;

  /** Controlled editing state */
  isEditing?: boolean;

  /** Callback when edit button clicked */
  onEditToggle?: () => void;

  /** Content (view or edit mode) */
  children: ReactNode;

  /** Optional footer actions (Save/Cancel buttons) */
  actions?: ReactNode;

  /** Additional styling */
  className?: string;
}

/**
 * InfoPanel - Generic wrapper for view/edit content
 *
 * Provides consistent card UI with collapsible header and edit toggle pattern.
 * Designed for reusability across settings pages, dialogs, and admin panels.
 *
 * Features:
 * - Bordered card with header and content area
 * - Optional Edit button in header (when isEditable && !isEditing)
 * - Constrained content width for optimal readability
 * - Footer area for action buttons when in edit mode
 *
 * @example
 * <InfoPanel
 *   title="Basic Information"
 *   isEditable={canEdit}
 *   isEditing={isEditing}
 *   onEditToggle={() => setIsEditing(true)}
 *   actions={isEditing && <><Button>Save</Button><Button>Cancel</Button></>}
 * >
 *   {isEditing ? <EditForm /> : <ReadView />}
 * </InfoPanel>
 */
export function InfoPanel({
  title,
  subtitle,
  isEditable = true,
  isEditing = false,
  onEditToggle,
  children,
  actions,
  className = '',
}: InfoPanelProps) {
  const showEditButton = isEditable && !isEditing && onEditToggle;

  return (
    <ThemedCard className={`overflow-hidden ${className}`}>
      {/* Header */}
      <div className="px-4 py-3 bg-zinc-50 dark:bg-zinc-800/50 border-b border-zinc-200 dark:border-zinc-700">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
            {subtitle && (
              <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">{subtitle}</p>
            )}
          </div>
          {Boolean(showEditButton) && (
            <button
              onClick={onEditToggle}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-sky-600 dark:text-sky-400 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors"
              aria-label={`Edit ${title}`}
            >
              <Edit2 className="h-4 w-4" />
              <span>Edit</span>
            </button>
          )}
        </div>
      </div>

      {/* Content - constrained width for readability */}
      <div className="px-4 py-4">{children}</div>

      {/* Footer actions (Save/Cancel buttons) */}
      {actions !== undefined && (
        <div className="px-4 py-3 bg-zinc-50 dark:bg-zinc-800/50 border-t border-zinc-200 dark:border-zinc-700">
          <div className="flex items-center gap-3">{actions}</div>
        </div>
      )}
    </ThemedCard>
  );
}
